import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  CheckCircle,
  ChevronDown,
  Eye,
  EyeOff,
  Calendar,
} from 'lucide-react';

const Toggle = ({ value, onChange }) => (
  <button
    onClick={onChange}
    style={{
      width: '44px', height: '24px', borderRadius: '12px',
      background: value ? '#2563EB' : '#CBD5E1',
      border: 'none', cursor: 'pointer', position: 'relative',
      transition: 'background 0.2s', flexShrink: 0,
    }}
  >
    <span style={{
      position: 'absolute', width: '18px', height: '18px',
      background: '#fff', borderRadius: '50%', top: '3px',
      left: value ? '23px' : '3px', transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
    }} />
  </button>
);

const Checkbox = ({ checked, onChange }) => (
  <button
    onClick={onChange}
    style={{
      width: '18px', height: '18px', borderRadius: '4px',
      border: checked ? 'none' : '1.5px solid #CBD5E1',
      background: checked ? '#2563EB' : '#fff',
      cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0, padding: 0,
    }}
  >
    {checked && (
      <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
        <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </button>
);

export default function ShareFile() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const file = state?.file;
  const fileName = file?.file_name || file?.name || 'No file';

  const [shareLink, setShareLink] = useState('');
  const [accessLevel, setAccessLevel] = useState('');
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [linkCreated, setLinkCreated] = useState(false);

  const [permissions, setPermissions] = useState({
    view: false,
    download: false,
    edit: false,
    reshare: false,
  });

  const togglePermission = (key) => {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCreateLink = () => {
    if (!shareLink) return alert('Please enter a share link first.');
    navigator.clipboard.writeText(shareLink);
    setLinkCreated(true);
    setTimeout(() => setLinkCreated(false), 3000);
  };

  const permissionList = [
    { key: 'view',     label: 'View',     description: 'Can view file online' },
    { key: 'download', label: 'Download', description: 'Can download file' },
    { key: 'edit',     label: 'Edit',     description: 'Can upload new versions' },
    { key: 'reshare',  label: 'Reshare',  description: 'Can share with others' },
  ];

  return (
    <div style={{
      background: '#ffffff', minHeight: '100vh', padding: '28px',
      fontFamily: "'Inter','Segoe UI',sans-serif", color: '#0F172A',
    }}>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '17px', fontWeight: 700, color: '#0F172A', margin: '0 0 8px' }}>
          Share File
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#64748B' }}>
          <ArrowLeft size={13} color="#94A3B8" />
          <span
            onClick={() => navigate(-1)}
            style={{ color: '#2563EB', fontWeight: 600, cursor: 'pointer' }}
          >
            My Drive
          </span>
          <span>&gt;</span>
          <span style={{ color: '#0F172A', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {fileName}
          </span>
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: '32px', alignItems: 'start' }}>

        {/* LEFT: Share Settings */}
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', marginBottom: '20px' }}>
            Share Settings
          </h2>

          {/* Share Link */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', color: '#64748B', marginBottom: '6px', fontWeight: 500 }}>
              Share Link
            </label>
            <div style={{
              display: 'flex', alignItems: 'center',
              border: '1px solid #E2E8F0', borderRadius: '8px',
              overflow: 'hidden', background: '#fff',
            }}>
              <input
                type="text"
                placeholder="Paste or enter your share link here..."
                value={shareLink}
                onChange={(e) => setShareLink(e.target.value)}
                style={{
                  flex: 1, padding: '10px 12px', border: 'none',
                  outline: 'none', fontSize: '14px', color: '#0F172A',
                  background: 'transparent', fontFamily: 'monospace',
                }}
              />
              <button
                onClick={handleCopyLink}
                style={{ background: 'none', border: 'none', padding: '0 12px', cursor: 'pointer' }}
              >
                {copiedLink
                  ? <CheckCircle size={16} color="#16A34A" />
                  : <Copy size={16} color="#94A3B8" />
                }
              </button>
            </div>
          </div>

          {/* Access Level */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', color: '#64748B', marginBottom: '6px', fontWeight: 500 }}>
              Access Level
            </label>
            <div style={{ position: 'relative' }}>
              <select
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
                style={{
                  width: '100%', padding: '10px 36px 10px 12px',
                  border: '1px solid #E2E8F0', borderRadius: '8px',
                  fontSize: '14px', color: accessLevel ? '#0F172A' : '#94A3B8',
                  background: '#fff', appearance: 'none',
                  cursor: 'pointer', outline: 'none',
                }}
              >
                <option value="" disabled>Select access level...</option>
                <option value="view">View Only</option>
                <option value="comment">Can Comment</option>
                <option value="edit">Can Edit</option>
                <option value="full">Full Access</option>
              </select>
              <ChevronDown size={16} color="#94A3B8" style={{
                position: 'absolute', right: '12px', top: '50%',
                transform: 'translateY(-50%)', pointerEvents: 'none',
              }} />
            </div>
          </div>

          {/* Password Protection */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <label style={{ fontSize: '14px', color: '#64748B', fontWeight: 500 }}>Password Protection</label>
              <Toggle value={passwordEnabled} onChange={() => {
                setPasswordEnabled(!passwordEnabled);
                if (passwordEnabled) setPassword('');
              }} />
            </div>
            {passwordEnabled && (
              <div style={{
                display: 'flex', alignItems: 'center',
                border: '1px solid #E2E8F0', borderRadius: '8px',
                overflow: 'hidden', background: '#fff',
              }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter a password..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{
                    flex: 1, padding: '10px 12px', border: 'none',
                    outline: 'none', fontSize: '14px', color: '#0F172A',
                    background: 'transparent',
                  }}
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ background: 'none', border: 'none', padding: '0 10px', cursor: 'pointer' }}
                >
                  {showPassword ? <EyeOff size={16} color="#94A3B8" /> : <Eye size={16} color="#94A3B8" />}
                </button>
                <button
                  onClick={() => password && navigator.clipboard.writeText(password)}
                  style={{
                    background: 'none', border: 'none',
                    borderLeft: '1px solid #E2E8F0',
                    padding: '0 10px', cursor: 'pointer',
                    height: '40px', display: 'flex', alignItems: 'center',
                  }}
                >
                  <Copy size={16} color="#94A3B8" />
                </button>
              </div>
            )}
          </div>

          {/* Expires On */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', color: '#64748B', marginBottom: '6px', fontWeight: 500 }}>
              Expires On (Optional)
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                style={{
                  width: '100%', padding: '10px 36px 10px 12px',
                  border: '1px solid #E2E8F0', borderRadius: '8px',
                  fontSize: '14px', color: expiryDate ? '#0F172A' : '#94A3B8',
                  background: '#fff', outline: 'none',
                  cursor: 'pointer', boxSizing: 'border-box',
                }}
              />
              <Calendar size={16} color="#94A3B8" style={{
                position: 'absolute', right: '12px', top: '50%',
                transform: 'translateY(-50%)', pointerEvents: 'none',
              }} />
            </div>
          </div>

          {/* Watermark */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '14px', color: '#64748B', fontWeight: 500, margin: '0 0 2px' }}>Watermark</p>
                <p style={{ fontSize: '14px', color: '#94A3B8', margin: 0 }}>Add watermark to downloaded files.</p>
              </div>
              <Toggle value={watermarkEnabled} onChange={() => setWatermarkEnabled(!watermarkEnabled)} />
            </div>
          </div>

          {/* Allow Download */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ fontSize: '14px', color: '#64748B', fontWeight: 500 }}>Allow Download</label>
              <Toggle value={allowDownload} onChange={() => setAllowDownload(!allowDownload)} />
            </div>
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            paddingTop: '20px', borderTop: '1px solid #E2E8F0',
          }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                padding: '0 24px', border: '1px solid #E2E8F0',
                borderRadius: '10px', background: '#fff', height: '44px',
                fontSize: '14px', fontWeight: 600, color: '#64748B', cursor: 'pointer',
                fontFamily: "'Inter','Segoe UI',sans-serif",
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateLink}
              style={{
                padding: '0 24px', border: 'none', height: '44px',
                borderRadius: '10px',
                background: linkCreated ? '#16A34A' : '#2563EB',
                fontSize: '14px', fontWeight: 600, color: '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                gap: '8px', transition: 'background 0.2s',
                fontFamily: "'Inter','Segoe UI',sans-serif",
              }}
              onMouseEnter={e => { if (!linkCreated) e.currentTarget.style.background = '#1D4ED8'; }}
              onMouseLeave={e => { if (!linkCreated) e.currentTarget.style.background = '#2563EB'; }}
            >
              {linkCreated ? <><CheckCircle size={16} /> Link Copied!</> : 'Create Share Link'}
            </button>
          </div>
        </div>

        {/* RIGHT: Permissions */}
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', marginBottom: '20px' }}>
            Permissions
          </h2>
          <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
            {permissionList.map((perm, index) => (
              <div
                key={perm.key}
                onClick={() => togglePermission(perm.key)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                  padding: '14px 16px',
                  borderBottom: index < permissionList.length - 1 ? '1px solid #E2E8F0' : 'none',
                  background: '#fff', cursor: 'pointer',
                }}
              >
                <div style={{ marginTop: '2px' }}>
                  <Checkbox checked={permissions[perm.key]} onChange={() => togglePermission(perm.key)} />
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A', margin: '0 0 2px' }}>{perm.label}</p>
                  <p style={{ fontSize: '14px', color: '#94A3B8', margin: 0 }}>{perm.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '16px', background: '#EFF6FF',
            border: '1px solid #BFDBFE', borderRadius: '8px',
            padding: '12px 14px', fontSize: '14px',
            color: '#1E40AF', lineHeight: '1.5',
          }}>
            Permissions apply to anyone who accesses the share link. You can revoke access at any time.
          </div>
        </div>
      </div>
    </div>
  );
}